import mixpanel from 'mixpanel-browser'
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react'
import { isBrowser } from '../utils/generic'
import { EVENT_NAMES, EVENT_LOCATIONS } from '../utils/constants'
import { getUserProfileProperties } from '../utils/mixpanelUtils'
import { useGrowthbookFeature } from '../hooks/useGrowthbook'
import { useOrientation } from '../hooks/useOrientation'
import { pushDataLayer } from '../utils/pushDataLayer'

const EventTrackingContext = createContext()

export const EventTrackingProvider = ({ children }) => {
  const [mixpanelLoaded, setMixpanelLoaded] = useState()
  const trackingContextRef = useRef({
    game_id: null,
    game_type: null,
  })

  const eventTrackingEnabled = useGrowthbookFeature(
    'fe_igp_event_tracking_enabled'
  )
  
  const orientation = useOrientation()

  const trackEventInternal = useCallback((eventName, properties = {}, sendGAEvent = false) => {
    if (!eventTrackingEnabled) return
    if (!mixpanelLoaded) return
    if (!isBrowser() || !eventName) return

    const extendedProperties = {
      ...properties,
      env: process.env.GATSBY_MIXPANEL_ENV,
    }

    mixpanel.track(eventName, extendedProperties)

    if (sendGAEvent) {
      pushDataLayer({
        eventName,
        data: extendedProperties,
      })
    }
  }, [eventTrackingEnabled, mixpanelLoaded])

  const trackOrientationEvent = useCallback(
    (orientation_source = 'orientation_change') => {
      const properties = {
        ...trackingContextRef.current,
        orientation,
        pathname: window.location.pathname,
        orientation_source,
      }
      trackEventInternal(EVENT_NAMES.DEVICE_ORIENTATION_STATE, properties, true)
    },
    [trackEventInternal, orientation]
  )

  useEffect(() => {
    if (!eventTrackingEnabled) return

    mixpanel.init(process.env.GATSBY_MIXPANEL_PROJECT_TOKEN, {
      debug: process.env.GATSBY_MIXPANEL_ENV === 'dev',
      track_pageview:
        process.env.GATSBY_MIXPANEL_TRACK_PAGEVIEW === 'true'
          ? 'url-with-path'
          : false,
      persistence: 'localStorage',
      loaded: () => {
        setMixpanelLoaded(true)
      },
    })
  }, [eventTrackingEnabled])

  const isFirstTracking = useRef(true)
  useEffect(() => {
    const shouldTrack = eventTrackingEnabled && mixpanelLoaded && isBrowser() 
    if (!shouldTrack) return
    const orientation_source = isFirstTracking.current ? 'session_start' : 'orientation_change'
    isFirstTracking.current = false
    
    trackOrientationEvent(orientation_source)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- orientation dependency is needed for tracking orientation changes
  }, [eventTrackingEnabled, mixpanelLoaded, orientation, trackOrientationEvent])

  const updateCurrentGameId = useCallback((gameId) => {
    trackingContextRef.current.game_id = gameId
  }, [])

  const updateCurrentGameType = useCallback((gameType) => {
    trackingContextRef.current.game_type = gameType
  }, [])

  const getTrackingContext = useCallback(() => {
    return trackingContextRef.current
  }, [])

  const value = useMemo(
    () => ({
      eventTrackingEnabled,
      mixpanelLoaded,
      updateCurrentGameId,
      updateCurrentGameType,
      trackEventInternal,
      getTrackingContext,
    }),
    [
      eventTrackingEnabled,
      mixpanelLoaded,
      updateCurrentGameId,
      updateCurrentGameType,
      trackEventInternal,
      getTrackingContext,
    ]
  )

  return (
    <EventTrackingContext.Provider value={value}>
      {children}
    </EventTrackingContext.Provider>
  )
}

export const useEventTracking = () => {
  const context = useContext(EventTrackingContext)

  const {
    eventTrackingEnabled,
    mixpanelLoaded,
    updateCurrentGameId,
    updateCurrentGameType,
    trackEventInternal,
    getTrackingContext,
  } = context || {}

  const trackEvent = (eventName, properties = {}, sendGAEvent = false) => {
    trackEventInternal(eventName, properties, sendGAEvent)
  }

  const identifyUser = (user) => {
    if (!eventTrackingEnabled) return
    if (!mixpanelLoaded) return
    if (!user?.id) return

    mixpanel.identify(user?.id.toString())
    const userProfileProperties = getUserProfileProperties(user)
    mixpanel.people.set(userProfileProperties)
  }

  const deidentifyUser = () => {
    if (!eventTrackingEnabled) return
    if (!mixpanelLoaded) return

    mixpanel.reset()
  }

  const optUserIn = () => {
    if (!eventTrackingEnabled) return
    if (!mixpanelLoaded) return
    if (mixpanel.has_opted_in_tracking()) return

    mixpanel.opt_in_tracking()
  }

  const optUserOut = () => {
    if (!eventTrackingEnabled) return
    if (!mixpanelLoaded) return
    if (mixpanel.has_opted_out_tracking()) return

    mixpanel.opt_out_tracking()
  }

  return {
    trackEvent,
    identifyUser,
    deidentifyUser,
    optUserIn,
    optUserOut,
    updateCurrentGameId,
    updateCurrentGameType,
    getTrackingContext,
    EVENT_NAMES,
    EVENT_LOCATIONS,
    mixpanelLoaded,
  }
}
